// React and hooks
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';

// Redux
import { connect } from 'react-redux';

// i18n
import { useTranslation } from 'react-i18next';

// Fluent UI - Components
import { 
  Field, 
  Tag, 
  TagPicker, 
  TagPickerControl, 
  TagPickerGroup, 
  TagPickerInput, 
  TagPickerList, 
  TagPickerOption,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Label
} from '@fluentui/react-components';

// Fluent UI - Icons
import { DismissRegular } from '@fluentui/react-icons';

// Fluent UI - Types
import type { TagPickerProps } from '@fluentui/react-components';

// Project models
import { ProtocolPatient } from '../../../../models/Entities/Protocols/ProtocolPatient';

// Component functions
import * as ownFunctions from './ProtocolPatientSelector.Functions';

// Constants
const FIELD_MAX_WIDTH = 400;
const INVALID_PATIENT_STYLES = {
  backgroundColor: '#fde7e9',
  borderColor: '#d1343998',
  color: '#d61010ff'
};
const LOADING_SPINNER_STYLES = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 0'
};

export const ProtocolPatientSelectorComponent = (props: ownFunctions.Props) => {
  const { t } = useTranslation();
  
  // Local state for selected options and filter
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [patientFilterCriteria, setPatientFilterCriteria] = useState<string>('');
  
  // Ref to prevent circular updates
  const isInternalUpdate = useRef(false);
  const previousPatientsRef = useRef<ProtocolPatient[]>([]);

  // Load patients on component mount or when ProtocolId changes
  useEffect(() => {
    if (props.ProtocolId) {
      props.GetProtocolPatients(props.ProtocolId, '');
    }
  }, [props.ProtocolId, props.GetProtocolPatients]);

  // Function to compare arrays of patients
  const arePatientsEqual = useCallback((patients1: ProtocolPatient[] | undefined, patients2: ProtocolPatient[]): boolean => {
    if (!patients1 || patients1.length !== patients2.length) return false;
    const numbers1 = patients1.map(p => p.numberInProtocol).sort();
    const numbers2 = patients2.map(p => p.numberInProtocol).sort();
    return numbers1.every((num, index) => num === numbers2[index]);
  }, []);

  // Synchronize selectedOptions when ProtocolPatientsSelectedNumber changes (from parent)
  useEffect(() => {
    // Avoid update if the change came from this component
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (!props.ProtocolPatients || props.ProtocolPatients.length === 0) {
      return;
    }

    if (props.ProtocolPatientsSelectedNumber && props.ProtocolPatientsSelectedNumber.length > 0) {
      // Only update if it really changed
      if (JSON.stringify(props.ProtocolPatientsSelectedNumber.sort()) !== JSON.stringify(selectedOptions.sort())) {
        setSelectedOptions(props.ProtocolPatientsSelectedNumber);
      }
    } else {
      // Clear selection if there's no selection in props
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
      }
    }
  }, [props.ProtocolPatientsSelectedNumber, props.ProtocolPatients]);

  // Memoize available TagPicker options (filtered by search and selection)
  const tagPickerOptions = useMemo(() => {
    if (!props.ProtocolPatients || props.ProtocolPatients.length === 0) {
      return [];
    }
    
    return props.ProtocolPatients
      .map((patient) => patient.numberInProtocol)
      .filter((patientNumber: string) => 
        !selectedOptions.includes(patientNumber) && 
        patientNumber.toLowerCase().includes(patientFilterCriteria.toLowerCase())
      );
  }, [props.ProtocolPatients, selectedOptions, patientFilterCriteria]);

  // Callback to handle search filter
  const onPatientKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    setPatientFilterCriteria(e.currentTarget.value);
  }, []);

  // Callback to handle option selection
  const onOptionSelect: TagPickerProps["onOptionSelect"] = useCallback((e, data) => {
    setPatientFilterCriteria(""); // Clear filter on selection
    
    if (data.value === "no-options") {
      setSelectedOptions([]);
      isInternalUpdate.current = true;
      props.onSelectedProtocolPatient([]);
      return;
    }

    let newSelectedOptions: string[];
    
    // Detect if it's a removal (dismiss) or addition operation
    const isRemoving = data.selectedOptions.length < selectedOptions.length;
    
    if (isRemoving) {
      // If removing, use selectedOptions directly from event
      newSelectedOptions = data.selectedOptions;
    } else {
      // If adding
      if (!props.MultiselectEnable) {
        // Single selection mode: replace with new value
        newSelectedOptions = [data.value];
      } else {
        // Multi-selection mode
        newSelectedOptions = data.selectedOptions;
      }
    }

    setSelectedOptions(newSelectedOptions);

    // Get corresponding ProtocolPatient objects
    const selectedPatients = props.ProtocolPatients?.filter((patient: ProtocolPatient) => 
      newSelectedOptions.includes(patient.numberInProtocol)
    ) || [];

    // Compare with previous selection to avoid unnecessary updates
    if (!arePatientsEqual(selectedPatients, previousPatientsRef.current)) {
      previousPatientsRef.current = selectedPatients;
      isInternalUpdate.current = true;
      props.onSelectedProtocolPatient(selectedPatients);
    }
  }, [props.MultiselectEnable, props.ProtocolPatients, props.onSelectedProtocolPatient, arePatientsEqual, selectedOptions]);

  // Calculate derived states
  const hasNoPatients = !props.ProtocolPatients || props.ProtocolPatients.length === 0;
  const noProtocolSelected = props.ProtocolId === undefined || props.ProtocolId === 0;

  // Detect selected patients that don't exist in the protocol
  const invalidPatients = useMemo(() => {
    // Don't validate if no options selected or no protocol
    if (!selectedOptions.length || noProtocolSelected) return [];
    
    // If protocol exists but no patients loaded, all selected are invalid
    if (hasNoPatients) return selectedOptions;
    
    const validPatientNumbers = props.ProtocolPatients?.map(p => p.numberInProtocol) || [];
    return selectedOptions.filter(option => !validPatientNumbers.includes(option));
  }, [selectedOptions, props.ProtocolPatients, hasNoPatients, noProtocolSelected]);

  const hasInvalidPatients = invalidPatients.length > 0;

  // Helper functions for UI
  const getFieldLabel = () => props.showTitle !== false ? t('ProtocolPatientSelector.label') : undefined;

  const getValidationState = (): "error" | "warning" | "success" | "none" | undefined => {
    if (noProtocolSelected) return "error";
    if (hasInvalidPatients) return "warning";
    return undefined;
  };

  const getValidationMessage = (): string | undefined => {
    if (noProtocolSelected) {
      return t('ProtocolPatientSelector.noProtocolSelected');
    }
    if (hasInvalidPatients) {
      return t('ProtocolPatientSelector.invalidPatients', { patients: invalidPatients.join(', ') });
    }
    return undefined;
  };

  const getPlaceholder = (): string => {
    if (hasNoPatients) return t('ProtocolPatientSelector.noPatientsAvailable');
    if (selectedOptions.length === 0) return t('ProtocolPatientSelector.searchPlaceholder');
    return "";
  };

  // Conditional rendering: loading and error states
  if (props.isLoading) {
    return (
      <Field label={getFieldLabel()} style={{ maxWidth: FIELD_MAX_WIDTH }}>
        <div style={LOADING_SPINNER_STYLES}>
          <Spinner size="tiny" label={t('ProtocolPatientSelector.loadingPatients')} />
        </div>
      </Field>
    );
  }

  if (props.failOnLoading && props.error) {
    const errorMessage = props.error.ErrorMessage || 
      t('ProtocolPatientSelector.loadError');
    
    return (
      <Field label={getFieldLabel()} style={{ maxWidth: FIELD_MAX_WIDTH }}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{t('ProtocolPatientSelector.loadErrorTitle')}</MessageBarTitle>
            {errorMessage}
          </MessageBarBody>
        </MessageBar>
      </Field>
    );
  }

  // Main rendering
  return (
    <Field 
      label={getFieldLabel()}
      style={{ maxWidth: FIELD_MAX_WIDTH }}
      hint={props.noteText}
      validationState={getValidationState()}
      validationMessage={getValidationMessage()}
    >
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptions}
        disabled={hasNoPatients}
      >
        <TagPickerControl>
          <TagPickerGroup aria-label={t('ProtocolPatientSelector.selectedAria')}>
            {selectedOptions.map((option: string) => {
              const isInvalid = invalidPatients.includes(option);
              
              return (
                <Tag
                  key={option}
                  shape="rounded"
                  value={option}
                  dismissible
                  dismissIcon={<DismissRegular />}
                  style={isInvalid ? INVALID_PATIENT_STYLES : undefined}
                >
                  {option}
                </Tag>
              );
            })}
          </TagPickerGroup>
          <TagPickerInput 
            aria-label={t('ProtocolPatientSelector.inputAria')}
            onKeyUp={onPatientKeyUp}
            placeholder={getPlaceholder()}
          />
        </TagPickerControl>
        <TagPickerList>
          {tagPickerOptions.length > 0 ? (
            tagPickerOptions.map((option) => (
              <TagPickerOption
                value={option}
                key={option}
              >
                {option}
              </TagPickerOption>
            ))
          ) : (
            <TagPickerOption 
              value="no-options"
              text={hasNoPatients ? t('ProtocolPatientSelector.noPatientsAvailable') || '' : t('ProtocolPatientSelector.noMorePatients') || ''}
            >
              {hasNoPatients ? t('ProtocolPatientSelector.noPatientsAvailable') : t('ProtocolPatientSelector.noMorePatients')}
            </TagPickerOption>
          )}
        </TagPickerList>
      </TagPicker>
    </Field>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ProtocolPatientSelectorComponent as any);
